import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { TsDialogComponent } from 'tsui/lib/ts-dialog/ts-dialog.component';
import { DialogRef } from '@angular/cdk/dialog';
import { TsButtonModule, TSIconModule, TsSnackbarService } from 'tsui';
import { AsyncPipe, NgIf } from '@angular/common';
import { BehaviorSubject, finalize } from 'rxjs';
import { AssetManagementActions } from '../../state/asset-management.actions';
import { Store } from '@ngxs/store';
import { TsRxjsOperators } from '@shared/Utils/custom-rxjs-operators.utils';
import { AssetManagementState } from '../../state/asset-management.state';

@Component({
  selector: 'account-mapping-import-dialog',
  standalone: true,
  imports: [TsDialogComponent, TsButtonModule, NgIf, TSIconModule, AsyncPipe],
  templateUrl: './account-mapping-import-dialog.component.html',
  styleUrl: './account-mapping-import-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccountMappingImportDialogComponent {
  readonly store = inject(Store);
  readonly dialogRef = inject(DialogRef);
  readonly snackbar = inject(TsSnackbarService);
  readonly spreadsheetMimeTypes = [
    'application/vnd.ms-excel', // .xls
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
    'application/vnd.oasis.opendocument.spreadsheet', // .ods
    'text/csv', // .csv
    'text/tab-separated-values', // .tsv
    'application/vnd.google-apps.spreadsheet', // Google Sheets
    'application/x-iwork-numbers-sffnumbers', // .numbers
    'application/vnd.lotus-1-2-3', // .123
  ];
  readonly fileContainer = new BehaviorSubject({
    draggingOver: false,
    selectedFile: null,
  });
  readonly loading = new BehaviorSubject(false);

  get isSubmitable(): boolean {
    return this.fileContainer.value.selectedFile !== null;
  }

  get selectedAccountTreeId(): number {
    // For now we are only using the default tree id of 1. TODO pass the selected tree to the dialog.
    return this.store.selectSnapshot(AssetManagementState.accountTree).results[0].id;
  }

  submitForm() {
    if (!this.isSubmitable) return;

    this.loading.next(true);

    this.store
      .dispatch(
        new AssetManagementActions.ImportChartOfAccounts(
          this.selectedAccountTreeId,
          this.fileContainer.value.selectedFile,
        ),
      )
      .pipe(
        TsRxjsOperators.successAndErrorSnackbar(
          this.snackbar,
          'Chart of accounts imported successfully',
          'Failed to import chart of accounts',
        ),
        finalize(() => this.loading.next(false)),
      )
      .subscribe(() => {
        this.dialogRef.close(true);
      });
  }

  onDragOver(event: DragEvent): void {
    this.preventEventBubbling(event);

    this.fileContainer.next({
      ...this.fileContainer.value,
      draggingOver: true,
    });
  }

  onDragLeave(event: DragEvent): void {
    this.preventEventBubbling(event);

    this.fileContainer.next({
      ...this.fileContainer.value,
      draggingOver: false,
    });
  }

  onDrop(event: DragEvent | Event): void {
    this.preventEventBubbling(event);

    const files = event instanceof DragEvent ? event.dataTransfer?.files : (event.target as HTMLInputElement)?.files;
    const file = files?.[0];

    if (!file) return;

    if (!this.validateFileMimeType(files[0])) {
      this.snackbar.open('Invalid file type. Please upload a valid spreadsheet file.', 'error');
      return;
    }

    if (files?.length) {
      // Handle the files (e.g., upload or process them)
      this.fileContainer.next({
        draggingOver: false,
        selectedFile: file,
      });
    }
  }

  validateFileMimeType(file: File): boolean {
    return this.spreadsheetMimeTypes.includes(file?.type);
  }

  preventEventBubbling(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
  }

  clearFile() {
    this.fileContainer.next({
      draggingOver: false,
      selectedFile: null,
    });
  }
}
